import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Wine, Palmtree, Star, Users, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { BackgroundTexture } from "../components/BackgroundTexture";
import { Footer } from "../components/Footer";

const VENUE_LAYOUT_URL = "https://customer-assets.emergentagent.com/job_27178513-f6fb-45e5-994a-7b52c1854711/artifacts/r1r7sj64_argy%20table%20layout%20%282%29.png";

const features = [
  {
    icon: Crown,
    title: "Prime Location",
    desc: "Front-row tables with unobstructed views of world-class performances. Feel the energy from the best seat in the house."
  },
  {
    icon: Wine,
    title: "Premium Bottle Service",
    desc: "Curated selection of premium spirits, champagne, and bespoke cocktails delivered to your table all night."
  },
  {
    icon: Palmtree,
    title: "Private Cabanas",
    desc: "Your own secluded retreat with plush seating, dedicated service, and an intimate atmosphere steps from the action."
  },
  {
    icon: Star,
    title: "Priority Access",
    desc: "Skip the queue with exclusive VIP entrance, reserved parking, and a personal host to guide your evening."
  },
];

const tableCategories = [
  {
    name: "VVIP",
    tables: "V1 — V11",
    capacity: "6–10 guests",
    desc: "Elevated platform directly facing the DJ booth. The most coveted seats at RROV with premium bottle packages included.",
    accent: "#E8915A"
  },
  {
    name: "Members' Lounge",
    tables: "King, Joker, K1 — K4",
    capacity: "4–8 guests",
    desc: "Exclusive mid-tier tables flanking the DJ. Intimate setting with dedicated host and priority bar service.",
    accent: "#D4A574"
  },
  {
    name: "Cabanas",
    tables: "C1 — C6",
    capacity: "8–12 guests",
    desc: "Private open-air cabanas lining both sides of the venue. Perfect for groups seeking privacy with a view of the action.",
    accent: "#C68B6A"
  },
  {
    name: "Queen",
    tables: "Q1 — Q2",
    capacity: "4–6 guests",
    desc: "Tucked beside the VVIP section with a side-stage perspective. An intimate option for smaller groups.",
    accent: "#B87A5E"
  },
];

export default function VIPPage() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.getSettings().then((s) => setSettings(s.data)).catch(console.error);
  }, []);

  return (
    <div data-testid="vip-page" className="min-h-screen content-layer">
      <BackgroundTexture />
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative z-10">

        {/* Hero Intro */}
        <div className="py-8 lg:py-12 max-w-3xl mx-auto text-center">
          <p className="text-[11px] font-medium tracking-[0.15em] text-white/60 uppercase mb-3">Exclusive Access</p>
          <h1 className="font-syne text-3xl lg:text-5xl font-semibold text-white mb-4">The Ultimate Way to Experience RROV</h1>
          <p className="text-white/70 text-base lg:text-lg leading-relaxed">
            Reserve a private table, enjoy dedicated service, and make every night unforgettable. Your table. Your rules.
          </p>
        </div>

        {/* Feature Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {features.map((item, idx) => {
            const Icon = item.icon;
            return (
              <div key={idx} className="panel-light p-7 rounded-md group hover:shadow-lg transition-shadow">
                <div className="w-10 h-10 rounded-full bg-[#A04D1F]/10 flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#A04D1F]" />
                </div>
                <h3 className="font-syne text-base font-semibold text-[#2D1810] mb-2">{item.title}</h3>
                <p className="text-sm text-[#2D1810]/60 leading-relaxed">{item.desc}</p>
              </div>
            );
          })}
        </div>

        {/* Venue Layout */}
        <div className="mb-14">
          <div className="text-center mb-4">
            <p className="text-[11px] font-medium tracking-[0.15em] text-white/60 uppercase mb-2">Venue Map</p>
            <h2 className="font-syne text-2xl lg:text-3xl font-semibold text-white">Choose Your Spot</h2>
          </div>
          <div className="max-w-4xl mx-auto overflow-hidden rounded-md">
            <img
              src={VENUE_LAYOUT_URL}
              alt="RROV Venue Table Layout"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* Table Categories */}
        <div className="mb-14">
          <div className="text-center mb-6">
            <p className="text-[11px] font-medium tracking-[0.15em] text-white/60 uppercase mb-2">Table Options</p>
            <h2 className="font-syne text-2xl lg:text-3xl font-semibold text-white">Find Your Fit</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {tableCategories.map((cat, idx) => (
              <div key={idx} className="panel-light rounded-md overflow-hidden">
                <div className="h-1.5" style={{ backgroundColor: cat.accent }} />
                <div className="p-7">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-syne text-lg font-semibold text-[#2D1810]">{cat.name}</h3>
                    <span className="text-xs font-medium text-[#A04D1F] bg-[#A04D1F]/10 px-3 py-1 rounded-full">{cat.capacity}</span>
                  </div>
                  <p className="text-xs text-[#2D1810]/40 font-medium uppercase tracking-wider mb-2">Tables: {cat.tables}</p>
                  <p className="text-sm text-[#2D1810]/65 leading-relaxed">{cat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Booking Section */}
        <div className="panel-light p-8 lg:p-12 rounded-md mb-10">
          <div className="text-center mb-6">
            <h2 className="font-syne text-xl font-semibold text-[#2D1810] mb-2">Reserve Your Table</h2>
          </div>
          {settings?.vip_iframe_code ? (
            <div data-testid="vip-iframe" className="min-h-[500px]" dangerouslySetInnerHTML={{ __html: settings.vip_iframe_code }} />
          ) : (
            <div className="text-center py-10">
              <p className="text-[#2D1810]/60 mb-6">VIP table reservations are open. Get in touch to book your experience.</p>
              <Link
                to="/contact"
                data-testid="vip-contact-btn"
                className="btn-dark px-10 py-4 text-sm font-medium rounded-md inline-flex items-center justify-center gap-2"
              >
                Contact Us <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>

      </div>
      <Footer settings={settings} />
    </div>
  );
}
